import {cookieToObject, logout} from './auth';
import {ucFirst} from './misc';

const httpGet = async (url, signal = null, headers = {}, scope = '*') => {
    const request = buildRequest(url, {method: 'GET', headers: headers, body: {}}, scope, signal);
    return await processJsonResponse(request);
};

const httpGetBlob = async (url, signal = null, headers = {}, scope = '*') => {
    const request = buildRequest(url, {method: 'GET', headers, body: {}}, scope, signal);
    return await processBlobResponse(request);
}

const httpPost = async (url, body = '', signal = null, headers = {}, scope = '*') => {
    const request = buildRequest(url, {method: 'POST', headers: headers, body: body}, scope, signal);
    return await processJsonResponse(request);
};

const httpPut = async (url, body = '', signal = null, headers = {}, scope = '*') => {
    const request = buildRequest(url, {method: 'PUT', headers: headers, body: body}, scope, signal);
    return await processJsonResponse(request);
};

const httpDelete = async (url, signal = null, headers = {}, scope = '*') => {
    const request = buildRequest(url, {method: 'DELETE', headers: headers, body: {}}, scope, signal);
    return await processJsonResponse(request);
};

const httpPostFile = async (url, body = '', signal = null, headers = {}, scope = '*') => {
    const request = buildRequest(url, {method: 'POST_FILE', headers: headers, body: body}, scope, signal);
    return await processJsonResponse(request);
};

/**
 * Universal request wrapper.
 *
 * @param {string} endPoint
 * @param {string} method
 * @param {Object} headers
 * @param {Object} body
 * @param {string} scope
 * @param {AbortSignal} signal
 */
const buildRequest = (endPoint, {method, headers = {}, body = {}}, scope, signal = null) => {
    const cookies = cookieToObject();
    // Staff will save their initial hash to the cookie and not update it as they jump from different member profiles to masquerade as.
    // Members will update their profile hash everytime they switch their profile.
    let profileHash = cookies["profile_hash"] ?? null;

    method = method.toUpperCase();

    if (!cookies.hasOwnProperty('is_authenticated')) {
        // If is_authenticated is not present in cookies, redirect to login page.
        window.location.replace('/login');
        return;
    }

    switch (method) {
        case 'GET': {
            return fetch(endPoint, {
                method: method,
                credentials: 'include',
                headers: {
                    ...headers,
                    'Accept': 'application/json',
                    'Profile': profileHash,
                },
                signal: signal,
            })
                .then(response => response)
                .catch(error => {
                    // User friendly error message when there is no internet connection.
                    if (error.message === 'Failed to fetch') {
                        error.message = 'Connection failed.';
                    }
                    throw error;
                });
        }

        case 'DELETE':
        case 'POST':
        case 'PUT': {
            return fetch(endPoint, {
                signal: signal,
                method: method,
                credentials: 'include',
                headers: {
                    ...headers,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Profile': profileHash,
                },
                body: JSON.stringify(body),
            })
                .then(response => response)
                .catch(error => {
                    throw error;
                });
        }

        case 'POST_FILE':
            {
            return fetch(endPoint, {
                signal: signal,
                method: 'POST',
                credentials: 'include',
                headers: {
                    ...headers,
                    'Accept': 'application/json',
                    'Profile': profileHash,
                },
                body: body,
            })
                .then(response => response)
                .catch(error => {
                    throw error;
                });
        }
        default:
            throw new Error('Invalid request method.');
    }
};

//region Internal Functions
const processJsonResponse = async (requestPromise) => {
    if (!requestPromise) return;

    let data, error, response, responseJson, responseText;

    try {
        response = await requestPromise;

        if (response.ok) {
            responseText = await response.text();

            // Handle response without body
            data = responseText ? JSON.parse(responseText) : responseText;

            return data;
        } else if (response.status === 401) {
            await logout();
        } else if (response.status === 422) {
            error = await response.json();

            error = processErrorMessages(error);

            throw error;
        } else if (response.status === 403) {
            // Hide error message when response comes back with 403 status.
        } else {
            responseJson = await response.json();
            responseJson.status_code = response.status;
            throw responseJson;
        }
    } catch (error) {
        if (error.name !== 'AbortError') {
            throw error;
        }
    }
};

const processBlobResponse = async (requestPromise) => {
    if (!requestPromise) return;

    let error, response, responseJson, blob;

    try {
        response = await requestPromise;

        if (response.ok) {
            blob = await response.blob();
            return blob;
        } else if (response.status === 401) {
            await logout();
        } else if (response.status === 422) {
            error = await response.json();

            error = processErrorMessages(error);

            throw error;
        } else {
            responseJson = await response.json();
            throw responseJson;
        }
    } catch (error) {
        if (error.name !== 'AbortError') {
            throw error;
        }
    }
};

const processErrorMessages = (error) => {
    let errorMessage = error.message;

    if (error.errors) {
        const errors = error.errors;
        errorMessage += ' ';

        for (let [key, value] of Object.entries(errors)) {
            key = key.split('_').map(value => ucFirst(value)).join(' ');
            errorMessage += key + ': ' + value.join('; ');
        }
        error.message = errorMessage;
    }

    return error;
};

//endregion

export {buildRequest, httpGet, httpPost, httpPostFile, httpPut, httpDelete, httpGetBlob};
